type FaviconSet = Record<string, string>;

interface Favicons {
  synvest: FaviconSet;
  trustus: FaviconSet;
}

type BrandName = keyof Favicons | null;

let faviconsSet = false; // Flag to prevent multiple executions

function createOrUpdateFaviconElement(
  id: string,
  rel: string,
  sizes: string | null = null,
  type: string | null = null
) {
  let element = document.getElementById(id) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement("link");
    element.id = id;
    document.head.appendChild(element);
  }

  if (rel) element.rel = rel;
  if (sizes) element.sizes = sizes;
  if (type) element.type = type;

  return element;
}

function normalizeBrand(value: unknown): BrandName {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();

  // only allow known brands
  if (v === "synvest" || v === "trustus") return v;
  return null;
}

function getBrand(): BrandName {
  // 1) runtime injected value (best for deployments)
  const fromCustomEnv = normalizeBrand((window as any)?.customEnv?.current_env);
  if (fromCustomEnv) return fromCustomEnv;

  // 2) compile-time Vite env var (best for local/dev + simple deploys)
  const fromVite = normalizeBrand(import.meta.env.VITE_ENV);
  if (fromVite) return fromVite;

  return null;
}

function setFavicons() {
  if (faviconsSet) return;
  faviconsSet = true;

  const brand = normalizeBrand(import.meta.env.VITE_ENV);

  const favicons: Favicons = {
    synvest: {
      favicon: "/icons/synvest/favicon-32x32.png",
      appleTouchIcon: "/icons/synvest/apple-touch-icon.png",
      favicon16: "/icons/synvest/favicon-16x16.png",
      androidChrome192: "/icons/synvest/android-chrome-192x192.png",
      androidChrome512: "/icons/synvest/android-chrome-512x512.png",
      siteManifest: "/icons/synvest/site.webmanifest",
    },
    trustus: {
      favicon: "/icons/trustus/favicon.ico",
      appleTouchIcon: "/icons/trustus/apple-touch-icon.png",
      favicon16: "/icons/trustus/favicon-16x16.png",
      androidChrome192: "/icons/trustus/android-chrome-192x192.png",
      androidChrome512: "/icons/trustus/android-chrome-512x512.png",
      siteManifest: "/icons/trustus/site.webmanifest",
    },
  };

  const selectedFavicons: FaviconSet | null =
    brand && favicons[brand] ? favicons[brand] : null;

  const cacheBuster = `?v=${Date.now()}`;

  const updateFaviconAttribute = (
    id: string,
    href: string,
    rel: string,
    sizes: string | null = null,
    type: string | null = null
  ) => {
    const element = createOrUpdateFaviconElement(id, rel, sizes, type);
    element.setAttribute("href", href + cacheBuster);
  };

  if (!selectedFavicons) {
    console.warn("[favicons] No favicons selected. brand=", brand, "customEnv=", (window as any)?.customEnv);
    return;
  }

  // favicon type should match file type
  if (brand === "trustus") {
    updateFaviconAttribute("favicon", selectedFavicons.favicon, "icon", null, "image/x-icon");
  } else {
    updateFaviconAttribute("favicon", selectedFavicons.favicon, "icon", null, "image/png");
  }

  updateFaviconAttribute("apple-touch-icon", selectedFavicons.appleTouchIcon, "apple-touch-icon", "180x180");
  updateFaviconAttribute("favicon-16x16", selectedFavicons.favicon16, "icon", "16x16", "image/png");
  updateFaviconAttribute("android-chrome-192x192", selectedFavicons.androidChrome192, "icon", "192x192", "image/png");
  updateFaviconAttribute("android-chrome-512x512", selectedFavicons.androidChrome512, "icon", "512x512", "image/png");
  updateFaviconAttribute("web-manifest", selectedFavicons.siteManifest, "manifest");
}

document.addEventListener("DOMContentLoaded", () => {
  if ((window as any)?.customEnv) {
    setFavicons();
  } else {
    console.warn("[favicons] window.customEnv not defined yet, waiting for customEnvReady");
  }
});

document.addEventListener("customEnvReady", () => {
  setFavicons();
});

setTimeout(() => {
  if (!faviconsSet && (window as any)?.customEnv) {
    console.warn("[favicons] Favicons not set after 5 seconds, setting them now");
    setFavicons();
  }
}, 5000);
